import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

// v3.2 buttons (redesign-v3.2/assets/style.css .btn, .ib; iOS
// DesignSystem/Components.swift): one ink primary per screen, soft
// secondary, quiet ghost, destructive in the record red. Pills, 44px on
// touch screens and 36px from 1024px. Icons are 16px.
//
// apps/web's V3Button (components/ui/v3/button.tsx) re-exports this button.
export const buttonVariants = cva(
  "inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-v3 text-[13.5px] font-semibold transition-[background-color,color,opacity,transform] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-v3-ink text-v3-on-ink shadow-[0_1px_2px_rgba(0,0,0,0.12)] hover:opacity-[0.88]",
        secondary:
          "bg-v3-surface-2 text-v3-text shadow-v3-hairline hover:bg-v3-surface-3",
        ghost:
          "text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text data-[state=open]:bg-v3-surface-2 data-[state=open]:text-v3-text",
        destructive:
          "bg-v3-rec-tint text-v3-rec-text hover:bg-[color-mix(in_srgb,var(--v3-rec)_20%,transparent)]",
      },
      size: {
        default: "h-11 px-4 lg:h-9 lg:px-3.5",
        lg: "h-12 px-5 text-[15px]",
        icon: "size-11 lg:size-9",
      },
    },
    defaultVariants: { variant: "secondary", size: "default" },
  },
);

export type ButtonProps = React.ComponentPropsWithRef<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element (e.g. a router link) with the button look. */
    asChild?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  type,
  asChild = false,
  ...props
}: ButtonProps): React.JSX.Element {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      data-slot="button"
      type={asChild ? undefined : (type ?? "button")}
      {...props}
    />
  );
}

// An icon-only button: the label is required, for screen readers and as the
// hover tooltip.
export function IconButton({
  label,
  variant = "ghost",
  title,
  ...props
}: Omit<ButtonProps, "size"> & { label: string }): React.JSX.Element {
  return (
    <Button
      aria-label={label}
      size="icon"
      title={title ?? label}
      variant={variant}
      {...props}
    />
  );
}
